import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { MobilizationAuditEvent } from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';

@Component({
    selector: 'app-mobilization-history',
    standalone: true,
    templateUrl: './mobilization-history.component.html',
    styleUrls: ['./mobilization-history.component.css'],
    imports: [
        CommonModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        TableModule,
        TagModule,
        TimelineModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class MobilizationHistoryComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    events: MobilizationAuditEvent[] = [];
    isLoading = false;

    constructor(
        private mobilizationService: MobilizationService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Mobilization', routerLink: '/fleet/mobilization' }, { label: 'History' }];
        this.loadEvents();
    }

    loadEvents(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }
        this.isLoading = true;
        this.mobilizationService.getAuditEvents(companyId).subscribe({
            next: response => {
                this.events = response.data ?? [];
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load mobilization history.' });
            }
        });
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Audit Events', value: this.events.length, hint: 'Lifecycle and assignment changes', tone: 'info' },
            { label: 'Approvals', value: this.events.filter(event => event.actionCode === 'APPROVE').length, hint: 'Approved records', tone: 'success' },
            { label: 'Rejections', value: this.events.filter(event => event.actionCode === 'REJECT').length, hint: 'Rejected records', tone: 'danger' },
            { label: 'Supervisor Changes', value: this.events.filter(event => event.actionCode === 'SUPERVISOR_CHANGE').length, hint: 'Supervisor assignment updates', tone: 'info' }
        ];
    }

    dateTimeLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y HH:mm', 'en-US') : '-';
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'ACTIVE':
            case 'COMPLETED':
                return 'success';
            case 'SUBMITTED':
            case 'APPROVED':
                return 'warning';
            case 'REJECTED':
            case 'CANCELLED':
                return 'danger';
            case 'DRAFT':
                return 'secondary';
            default:
                return 'info';
        }
    }

    actionLabel(actionCode?: string): string {
        return (actionCode || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
    }

    actionSeverity(actionCode?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (actionCode) {
            case 'APPROVE':
            case 'ACTIVATE':
            case 'COMPLETE':
                return 'success';
            case 'REJECT':
                return 'danger';
            case 'SUBMIT':
                return 'warning';
            case 'SUPERVISOR_CHANGE':
                return 'info';
            default:
                return 'secondary';
        }
    }
}
